import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary.tsx'

// Initialize Sentry BEFORE rendering React
// This must be done before createRoot() to catch errors
Sentry.init({
  // The DSN tells Sentry where to send error events
  dsn: import.meta.env.VITE_SENTRY_DSN,

  // Track which environment errors come from (development/staging/production)
  environment: import.meta.env.VITE_APP_ENV || 'unknown',

  // Only enable in production/staging - disable for local development
  enabled: !!import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_APP_ENV !== 'development',

  // Sample 100% of errors in development, 10% in production to save quota
  // This is for transaction sampling (performance monitoring)
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,

  // Don't collect user IP addresses (GDPR-friendly)
  // Note: still sent via sendDefaultPii but we override here
  sendDefaultPii: false,

  // Filter out errors that aren't useful to track
  beforeSend(event: Sentry.ErrorEvent) {
    // Skip errors from browser extensions (they're not your problem)
    if (event.request?.url?.includes('chrome-extension://')) {
      return null
    }
    if (event.request?.url?.includes('moz-extension://')) {
      return null
    }

    // Skip ResizeObserver errors (harmless)
    if (event.exception?.values?.some((ex: { value?: string }) => ex.value?.includes('ResizeObserver'))) {
      return null
    }

    return event
  },

  // Ignore specific error patterns that aren't useful
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'chrome-extension://',
    'moz-extension://',
    // ResizeObserver error
    'ResizeObserver loop limit exceeded',
  ],

  // Release tracking (will be set by GitHub Actions during deploy)
  release: import.meta.env.VITE_APP_VERSION || undefined,
})

const root = createRoot(document.getElementById('root')!)
root.render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
